import { X, ChevronDown } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "@/context/app-context";
import { DAYS, toDateStr, getMonday } from "@/lib/helpers";
import type { ScheduleSlot } from "@/lib/types";

function makeEmptyWeek(): ScheduleSlot[] {
  return DAYS.map((day) => ({ day, workoutId: null }));
}

function slotsToMap(slots: ScheduleSlot[]): Record<string, string> {
  return slots.reduce((acc, slot) => {
    acc[slot.day] = slot.workoutId || "";
    return acc;
  }, {} as Record<string, string>);
}

export function ScheduleConfig() {
  const navigate = useNavigate();
  const { state, dispatch } = useApp();

  const [scheduleType, setScheduleType] = useState<"weekly" | "cadence" | "multiweek">(
    state.scheduleType as any
  );

  // Multi-week state
  const existingMW = state.multiWeekSchedule;
  const [mwCycleLength, setMwCycleLength] = useState(existingMW?.cycleLength || 2);
  const [mwWeeks, setMwWeeks] = useState<Record<string, string>[]>(() => {
    const weeks = existingMW?.weeks || [];
    const result: Record<string, string>[] = [];
    for (let i = 0; i < 4; i++) {
      result.push(weeks[i] ? slotsToMap(weeks[i]) : slotsToMap(makeEmptyWeek()));
    }
    return result;
  });
  const [mwEditingWeek, setMwEditingWeek] = useState(0);

  // Weekly state (for simple weekly mode)
  const [weeklyAssignments, setWeeklyAssignments] = useState<Record<string, string>>(
    slotsToMap(state.weeklySchedule)
  );

  // Cadence state
  const [cadenceCycleLength, setCadenceCycleLength] = useState(
    state.cadenceSchedule.cycleLengthDays || 14
  );

  const workoutOptions = Object.values(state.workouts);

  // Get the assignments for the currently active mode/week
  const activeAssignments =
    scheduleType === "multiweek" ? mwWeeks[mwEditingWeek] : weeklyAssignments;

  const setActiveAssignment = (day: string, workoutId: string) => {
    if (scheduleType === "multiweek") {
      const updated = [...mwWeeks];
      updated[mwEditingWeek] = { ...updated[mwEditingWeek], [day]: workoutId };
      setMwWeeks(updated);
    } else {
      setWeeklyAssignments({ ...weeklyAssignments, [day]: workoutId });
    }
  };

  const toggleDay = (day: string) => {
    const current = activeAssignments[day];
    if (current) {
      setActiveAssignment(day, "");
    } else {
      setActiveAssignment(day, workoutOptions.length > 0 ? workoutOptions[0].id : "");
    }
  };

  const selectedDays = DAYS.filter((day) => activeAssignments[day]);

  const getWorkoutColor = (workoutId: string): string => {
    if (!workoutId) return "#3F3F46";
    return "#FF6B35";
  };

  const handleSave = () => {
    dispatch({ type: "SET_SCHEDULE_TYPE", scheduleType });

    if (scheduleType === "weekly") {
      const schedule = DAYS.map((day) => ({
        day,
        workoutId: weeklyAssignments[day] || null,
      }));
      dispatch({ type: "SET_WEEKLY_SCHEDULE", schedule });
    } else if (scheduleType === "multiweek") {
      const weeks: ScheduleSlot[][] = [];
      for (let i = 0; i < mwCycleLength; i++) {
        weeks.push(
          DAYS.map((day) => ({
            day,
            workoutId: mwWeeks[i][day] || null,
          }))
        );
      }
      dispatch({
        type: "SET_MULTIWEEK_SCHEDULE",
        data: {
          cycleLength: mwCycleLength,
          weeks,
          currentWeek: 0,
          startDate: existingMW?.startDate || toDateStr(getMonday(new Date())),
        },
      });
    } else {
      dispatch({
        type: "SET_CADENCE_SCHEDULE",
        data: { cycleLengthDays: cadenceCycleLength },
      });
    }

    navigate(-1);
  };

  // When changing multi-week cycle length, ensure weeks array has enough entries
  const handleMwCycleLengthChange = (len: number) => {
    setMwCycleLength(len);
    if (mwEditingWeek >= len) setMwEditingWeek(0);
  };

  // Summary text
  const getSummary = () => {
    if (scheduleType === "weekly") {
      const days = DAYS.filter((d) => weeklyAssignments[d]).length;
      return `${days} training days per week • Weekly schedule`;
    }
    if (scheduleType === "multiweek") {
      const totalDays = mwWeeks
        .slice(0, mwCycleLength)
        .reduce((sum, w) => sum + DAYS.filter((d) => w[d]).length, 0);
      const avg = (totalDays / mwCycleLength).toFixed(1);
      return `~${avg} days/week avg • ${mwCycleLength}-week rotating cycle`;
    }
    return `Cadence schedule • ${cadenceCycleLength} day cycle`;
  };

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ backgroundColor: "#18181B", maxWidth: "430px", margin: "0 auto" }}
    >
      {/* Header */}
      <div
        className="flex items-center justify-between p-4"
        style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}
      >
        <div style={{ width: "44px" }} />
        <div style={{ color: "#F0F0F0", fontSize: "16px", fontWeight: 700 }}>
          Schedule Setup
        </div>
        <button
          onClick={() => navigate(-1)}
          style={{
            minWidth: "44px",
            minHeight: "44px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <X size={24} style={{ color: "#F0F0F0" }} />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-5 pb-24">
        {/* Schedule Type — 3 options */}
        <div className="mb-6">
          <label
            style={{
              color: "#71717A",
              fontSize: "12px",
              fontWeight: 600,
              display: "block",
              marginBottom: "12px",
              letterSpacing: "0.5px",
            }}
          >
            SCHEDULE TYPE
          </label>
          <div className="grid grid-cols-3 gap-2">
            {(
              [
                { key: "weekly", label: "Weekly", desc: "Same plan every week" },
                { key: "multiweek", label: "Multi-Week", desc: "Different plan each week, then repeats" },
                { key: "cadence", label: "Cadence", desc: "Rotate workouts regardless of day" },
              ] as const
            ).map((opt) => (
              <button
                key={opt.key}
                onClick={() => setScheduleType(opt.key)}
                className="p-3 rounded-xl text-left"
                style={{
                  backgroundColor: "#27272A",
                  border:
                    scheduleType === opt.key
                      ? "2px solid #FF6B35"
                      : "2px solid rgba(255,255,255,0.06)",
                }}
              >
                <div
                  style={{
                    color: scheduleType === opt.key ? "#FF6B35" : "#F0F0F0",
                    fontSize: "13px",
                    fontWeight: 700,
                    marginBottom: "4px",
                  }}
                >
                  {opt.label}
                </div>
                <div style={{ color: "#71717A", fontSize: "10px", lineHeight: "1.4" }}>
                  {opt.desc}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Multi-Week: Cycle Length */}
        {scheduleType === "multiweek" && (
          <div className="mb-6">
            <label
              style={{
                color: "#71717A",
                fontSize: "12px",
                fontWeight: 600,
                display: "block",
                marginBottom: "12px",
                letterSpacing: "0.5px",
              }}
            >
              WEEKS IN CYCLE
            </label>
            <div className="flex gap-2">
              {[2, 3, 4].map((len) => (
                <button
                  key={len}
                  onClick={() => handleMwCycleLengthChange(len)}
                  className="flex-1 py-3 rounded-lg"
                  style={{
                    backgroundColor: mwCycleLength === len ? "#FF6B35" : "#27272A",
                    color: mwCycleLength === len ? "#18181B" : "#71717A",
                    fontSize: "14px",
                    fontWeight: 700,
                    border:
                      mwCycleLength === len ? "none" : "1px solid rgba(255,255,255,0.06)",
                  }}
                >
                  {len} weeks
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Cadence: Cycle Length */}
        {scheduleType === "cadence" && (
          <div className="mb-6">
            <label
              style={{
                color: "#71717A",
                fontSize: "12px",
                fontWeight: 600,
                display: "block",
                marginBottom: "12px",
                letterSpacing: "0.5px",
              }}
            >
              CYCLE LENGTH (DAYS)
            </label>
            <div className="flex gap-2">
              {[7, 10, 14, 21].map((days) => (
                <button
                  key={days}
                  onClick={() => setCadenceCycleLength(days)}
                  className="flex-1 py-3 rounded-lg"
                  style={{
                    backgroundColor: cadenceCycleLength === days ? "#FF6B35" : "#27272A",
                    color: cadenceCycleLength === days ? "#18181B" : "#71717A",
                    fontSize: "14px",
                    fontWeight: 700,
                    border:
                      cadenceCycleLength === days
                        ? "none"
                        : "1px solid rgba(255,255,255,0.06)",
                  }}
                >
                  {days}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Multi-Week: Week Tabs */}
        {scheduleType === "multiweek" && (
          <div className="mb-5">
            <label
              style={{
                color: "#71717A",
                fontSize: "12px",
                fontWeight: 600,
                display: "block",
                marginBottom: "12px",
                letterSpacing: "0.5px",
              }}
            >
              EDIT WEEK
            </label>
            <div className="flex gap-2">
              {Array.from({ length: mwCycleLength }, (_, i) => {
                const weekDays = DAYS.filter((d) => mwWeeks[i][d]).length;
                return (
                  <button
                    key={i}
                    onClick={() => setMwEditingWeek(i)}
                    className="flex-1 py-3 rounded-lg flex flex-col items-center gap-1"
                    style={{
                      backgroundColor: mwEditingWeek === i ? "#FF6B35" : "#27272A",
                      color: mwEditingWeek === i ? "#18181B" : "#A1A1AA",
                      fontSize: "13px",
                      fontWeight: 700,
                      border:
                        mwEditingWeek === i
                          ? "none"
                          : "1px solid rgba(255,255,255,0.06)",
                    }}
                  >
                    <span>Week {i + 1}</span>
                    <span
                      style={{
                        fontSize: "10px",
                        fontWeight: 500,
                        opacity: 0.7,
                      }}
                    >
                      {weekDays} days
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Training Days (for weekly and multiweek modes) */}
        {scheduleType !== "cadence" && (
          <>
            <div className="mb-6">
              <label
                style={{
                  color: "#71717A",
                  fontSize: "12px",
                  fontWeight: 600,
                  display: "block",
                  marginBottom: "12px",
                  letterSpacing: "0.5px",
                }}
              >
                {scheduleType === "multiweek"
                  ? `TRAINING DAYS — WEEK ${mwEditingWeek + 1}`
                  : "TRAINING DAYS"}
              </label>
              <div className="grid grid-cols-7 gap-2">
                {DAYS.map((day, index) => {
                  const isSelected = !!activeAssignments[day];
                  return (
                    <button
                      key={index}
                      onClick={() => toggleDay(day)}
                      className="rounded-full flex items-center justify-center"
                      style={{
                        width: "44px",
                        height: "44px",
                        backgroundColor: isSelected ? "#FF6B35" : "#27272A",
                        color: isSelected ? "#18181B" : "#71717A",
                        fontSize: "14px",
                        fontWeight: 700,
                        border: isSelected ? "none" : "1px solid rgba(255,255,255,0.06)",
                      }}
                    >
                      {day.slice(0, 1)}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Workout Assignments */}
            {selectedDays.length > 0 && (
              <div className="mb-6">
                <label
                  style={{
                    color: "#71717A",
                    fontSize: "12px",
                    fontWeight: 600,
                    display: "block",
                    marginBottom: "12px",
                    letterSpacing: "0.5px",
                  }}
                >
                  {scheduleType === "multiweek"
                    ? `WORKOUT ASSIGNMENT — WEEK ${mwEditingWeek + 1}`
                    : "WORKOUT ASSIGNMENT"}
                </label>
                <div className="space-y-3">
                  {selectedDays.map((day) => (
                    <div
                      key={day}
                      className="p-4 rounded-xl"
                      style={{
                        backgroundColor: "#27272A",
                        border: "1px solid rgba(255,255,255,0.06)",
                      }}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div style={{ color: "#F0F0F0", fontSize: "14px", fontWeight: 700 }}>
                          {day}
                        </div>
                        <div className="relative">
                          <select
                            value={activeAssignments[day] || ""}
                            onChange={(e) => setActiveAssignment(day, e.target.value)}
                            className="rounded-lg appearance-none cursor-pointer"
                            style={{
                              backgroundColor: getWorkoutColor(activeAssignments[day]),
                              color:
                                activeAssignments[day] && state.workouts[activeAssignments[day]]
                                  ? "#18181B"
                                  : "#71717A",
                              fontSize: "13px",
                              fontWeight: 700,
                              padding: "8px 32px 8px 12px",
                              border: "none",
                            }}
                          >
                            <option value="">Rest Day</option>
                            {workoutOptions.map((workout) => (
                              <option key={workout.id} value={workout.id}>
                                {workout.name}
                              </option>
                            ))}
                          </select>
                          <ChevronDown
                            size={16}
                            style={{
                              position: "absolute",
                              right: "10px",
                              top: "50%",
                              transform: "translateY(-50%)",
                              pointerEvents: "none",
                              color:
                                activeAssignments[day] && state.workouts[activeAssignments[day]]
                                  ? "#18181B"
                                  : "#71717A",
                            }}
                          />
                        </div>
                      </div>

                      {/* Mini Preview Card */}
                      <div className="p-3 rounded-lg" style={{ backgroundColor: "#3F3F46" }}>
                        <div className="flex items-center justify-between">
                          <div>
                            <div style={{ color: "#A1A1AA", fontSize: "11px", marginBottom: "2px" }}>
                              Assigned Workout
                            </div>
                            <div style={{ color: "#F0F0F0", fontSize: "14px", fontWeight: 700 }}>
                              {activeAssignments[day] && state.workouts[activeAssignments[day]]
                                ? state.workouts[activeAssignments[day]].name
                                : "Rest Day"}
                            </div>
                          </div>
                          {activeAssignments[day] && (
                            <div
                              className="px-2 py-1 rounded"
                              style={{
                                backgroundColor: "#FF6B35",
                                color: "#18181B",
                                fontSize: "10px",
                                fontWeight: 700,
                              }}
                            >
                              Active
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {/* Summary Info */}
        <div
          className="p-4 rounded-lg"
          style={{ backgroundColor: "#1F4428", border: "1px solid #4ADE80" }}
        >
          <div className="flex items-center justify-between mb-2">
            <div style={{ color: "#4ADE80", fontSize: "12px", fontWeight: 600 }}>
              SCHEDULE SUMMARY
            </div>
          </div>
          <div style={{ color: "#A1A1AA", fontSize: "12px", lineHeight: "1.5" }}>
            {getSummary()}
          </div>
        </div>
      </div>

      {/* Bottom Save Button */}
      <div
        className="p-4"
        style={{
          backgroundColor: "#27272A",
          borderTop: "1px solid rgba(255,255,255,0.06)",
        }}
      >
        <button
          onClick={handleSave}
          className="w-full rounded-xl"
          style={{
            backgroundColor: "#FF6B35",
            color: "#18181B",
            fontSize: "16px",
            fontWeight: 700,
            padding: "16px",
            minHeight: "56px",
          }}
        >
          Save Schedule
        </button>
      </div>
    </div>
  );
}
